import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { noteKindLabel } from "@/components/audit/AuditNotes";
import { when } from "@/components/audit/audit-format";
import AuditBackLink from "@/components/audit/AuditBackLink";
import ReportMarkdown from "@/components/audit/ReportMarkdown";
import PageHeader from "@/components/shell/PageHeader";
import EmptyState from "@/components/ui/EmptyState";
import { Page, PageBody } from "@/components/ui/Page";
import { trpc } from "@/lib/trpc";
import { useWindowTitleDetail } from "@/lib/window-title";
import { workspaceModuleRedirect } from "@/lib/workspace-module-access";
import { useReloadForNewBuild } from "@/lib/stale-build";

// One Note (user story 61): a side analysis or memory note as the agent wrote
// it, rendered like a Report, with who synced it and when in the header. The
// trailing underscore in the file name keeps this page out of the tabs' layout.
export const Route = createFileRoute("/_authenticated/$orgSlug/audit_/notes/$noteId")({
  beforeLoad: async ({ params, context }) => {
    const viewer = await context.queryClient.ensureQueryData(
      trpc.organization.viewer.queryOptions(),
    );
    const target = workspaceModuleRedirect(viewer, params.orgSlug);
    if (target) throw redirect(target);
  },
  loader: ({ params, context }) =>
    context.queryClient.ensureQueryData(
      trpc.audit.note.queryOptions({ orgSlug: params.orgSlug, noteId: params.noteId }),
    ),
  errorComponent: NoteLoadError,
  component: NotePage,
});

function NoteLoadError({ error }: { error: unknown }) {
  const { orgSlug } = Route.useParams();
  if (useReloadForNewBuild(error)) return null;
  return (
    <Page>
      <PageHeader title="Audit" back={<AuditBackLink list="notes" orgSlug={orgSlug} />} />
      <PageBody>
        <EmptyState title="This Note could not be opened.">
          {error instanceof Error ? error.message : String(error)}
        </EmptyState>
      </PageBody>
    </Page>
  );
}

function NotePage() {
  const { orgSlug, noteId } = Route.useParams();
  const { data: note } = useSuspenseQuery(trpc.audit.note.queryOptions({ orgSlug, noteId }));
  useWindowTitleDetail(note.title);
  // The header carries the title; the Note's own first heading repeats it.
  return (
    <Page>
      <PageHeader
        back={<AuditBackLink list="notes" orgSlug={orgSlug} accountId={note.accountId} />}
        title={note.title}
        context={[noteKindLabel(note), note.uploadedByName, when(note.uploadedAt, true)]
          .filter(Boolean)
          .join(" · ")}
      />
      <PageBody>
        <ReportMarkdown markdown={note.content} skipLeadingHeading />
      </PageBody>
    </Page>
  );
}
